import React from "react";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { Link } from "react-router-dom";
import { Section, Kicker, SerifTitle, HairlineBrass } from "@/components/Section";
import { EditorialImage } from "@/components/EditorialImage";
import { MediaGallery } from "@/components/MediaGallery";
import { VideoGallerySection } from "@/components/VideoGallerySection";
import { HeroVideo, heroTextShadow } from "@/components/HeroVideo";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ArrowUpRight, Check } from "lucide-react";
import {
    PRINTS,
    STUDIOS,
    REVIEWS,
    LIFE_LEGACY_PACKAGES,
    LIFE_LEGACY_ADDONS,
    PRICING_REASSURANCE,
    PRICING_CUSTOM_NOTE,
    PRICING_BOOKING_NOTE,
} from "@/lib/data";
import { usePortfolio, useLifeVideo } from "@/lib/content";
import InvestmentSection, { AddOnsSection } from "@/components/Investment";

export default function LifeLegacy() {
    const s = STUDIOS.life;
    const PORTFOLIO = usePortfolio();
    const lifeVideoItems = useLifeVideo();
    const work = PORTFOLIO.filter((p) => ["Family", "Portraits", "Events"].includes(p.category)).slice(0, 8);
    const heirloom = PRINTS.slice(0, 3);
    const lifeReviews = [REVIEWS[1], REVIEWS[3], REVIEWS[4]].filter(Boolean);

    return (
        <div className="relative">
            <Seo {...SEO.lifeLegacy} />
            {/* HERO — brand-tone video, copy preserved */}
            <HeroVideo
                dataTestId="life-hero"
                ariaLabel="Life & Legacy Studio — portrait, family, and story photography by Elizaveta von Stuben Imagery."
            >
                <span
                    className="block font-sans uppercase text-[11px] sm:text-[12px]"
                    style={{ color: "#e6e1db", letterSpacing: "0.22em", ...heroTextShadow.light }}
                >
                    {s.kicker}
                </span>
                <h1
                    className="font-serif-display mt-3 sm:mt-4 text-4xl sm:text-6xl lg:text-7xl leading-[0.98] max-w-[18ch]"
                    style={{ color: "#fbf8f4", ...heroTextShadow.heavy }}
                >
                    {s.name}
                </h1>
                <p
                    className="mt-5 max-w-[60ch] text-sm sm:text-base leading-[1.75]"
                    style={{ color: "#f5f1eb", ...heroTextShadow.medium }}
                >
                    {s.tagline}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                    <Link to="/inquire?type=Story%20Session" data-testid="life-primary-cta" className="btn-request">
                        Request Availability
                    </Link>
                    <Link to="/portfolio/life-legacy" className="btn-request btn-request--sm btn-request--outline-light">
                        {s.ctaSecondary}
                    </Link>
                </div>
            </HeroVideo>

            {/* POSITIONING */}
            <Section spacing="loose">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <Kicker>{s.intent}</Kicker>
                        <SerifTitle className="mt-4">Refined photography for the people you love most.</SerifTitle>
                        <HairlineBrass className="mt-6" />
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="text-base leading-[1.95] text-foreground/85 max-w-[60ch]">
                            {s.long}
                        </p>
                        <p className="mt-5 text-sm text-muted-foreground max-w-[58ch]">
                            You do not need to know how to pose. You will be guided into looking and feeling like yourself — at your highest level.
                        </p>
                    </div>
                </div>
            </Section>

            {/* SERVICES */}
            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border">
                <Kicker>Inside the studio</Kicker>
                <SerifTitle className="mt-3">Six ways to be photographed.</SerifTitle>
                <p className="mt-4 text-sm text-muted-foreground max-w-[58ch]">
                    Click any service to begin a tailored inquiry. Maternity, motherhood, graduation, birthdays, and personal milestones live inside <em>Meaningful Life Moments</em>.
                </p>
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                    {s.services.map((sv, i) => (
                        <Link
                            key={sv.id}
                            to={`/inquire?type=${encodeURIComponent(sv.title)}`}
                            data-testid={`life-service-card-${sv.id}`}
                            className="group bg-background border border-border rounded-[2px] p-6 h-full flex flex-col transition-colors hover:border-[hsl(var(--lux-brass))]"
                        >
                            <div className="flex items-baseline justify-between">
                                <div className="kicker">0{i + 1}</div>
                                <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[hsl(var(--lux-brass))]" />
                            </div>
                            <h3 className="font-serif-display text-xl leading-tight mt-2 group-hover:text-foreground">{sv.title}</h3>
                            <Separator className="my-4 bg-[hsl(var(--lux-brass))]/40 w-8" />
                            <p className="text-sm text-muted-foreground leading-[1.7]">{sv.copy}</p>
                            <span className="mt-auto pt-5 text-[11px] tracking-[0.18em] uppercase text-muted-foreground group-hover:text-foreground">
                                Request availability →
                            </span>
                        </Link>
                    ))}
                </div>
            </Section>

            {/* INVESTMENT */}
            <InvestmentSection
                kicker="Investment · Life & Legacy"
                title="Four ways to begin."
                subtitle="From a polished personal portrait to a fully custom event commission. Every tier is custom quoted from the starting figure based on creative direction, scope, and delivery."
                packages={LIFE_LEGACY_PACKAGES}
                reassurance={PRICING_REASSURANCE}
                customNote={PRICING_CUSTOM_NOTE}
                bookingNote={PRICING_BOOKING_NOTE}
                className="bg-background"
            />

            {/* ADD-ONS — Add Motion to Your Story */}
            <AddOnsSection
                id="life-addons"
                kicker="Add Motion to Your Story"
                title="Add a short cinematic film."
                subtitle="Add a short cinematic film to preserve the feeling of the moment — perfect for proposals, families, celebrations, behind‑the‑scenes, and personal milestones."
                addons={LIFE_LEGACY_ADDONS}
                ctaLabel="Add Motion to My Session"
                ctaSessionType="Photo + Video Content"
                className="bg-[hsl(var(--lux-porcelain))]"
            />

            {/* PORTFOLIO */}
            <Section spacing="normal">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <Kicker>Selected Work</Kicker>
                        <SerifTitle className="mt-3">From the archive.</SerifTitle>
                    </div>
                    <Link to="/portfolio/life-legacy" className="hidden sm:inline-flex items-center text-xs tracking-[0.18em] uppercase lux-underline">
                        Open Life &amp; Legacy archive <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Link>
                </div>
                <MediaGallery
                    items={work.map((p, i) => ({
                        id: p.id,
                        src: p.imageHome || p.image,
                        alt: p.alt || p.title,
                        caption: p.title,
                        ratio: i % 2 === 0 ? 4 / 5 : 1,
                    }))}
                    desktopColumns={4}
                    initialCount={work.length}
                    defaultInquiryType="Story Session"
                    galleryId="life-selected-work"
                />
                <div className="mt-8 sm:hidden">
                    <Link to="/portfolio/life-legacy" className="inline-flex items-center text-xs tracking-[0.18em] uppercase lux-underline">
                        Open Life &amp; Legacy archive <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Link>
                </div>
            </Section>

            {/* PHASE 15: VIDEO (auto-hides until life-video gallery is published) */}
            <VideoGallerySection
                items={lifeVideoItems}
                testId="life-video"
                galleryId="life-video-gallery"
                inquiryType="Story Session"
                desktopColumns={3}
            />

            {/* HEIRLOOM / FINE ART */}
            <Section spacing="normal" className="bg-[hsl(var(--lux-porcelain))] border-t border-border">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-5">
                        <Kicker>Heirloom</Kicker>
                        <SerifTitle className="mt-3">Albums &amp; wall prints, made to be inherited.</SerifTitle>
                        <p className="mt-5 text-sm leading-[1.85] text-muted-foreground max-w-[44ch]">
                            Hand‑bound heirloom albums and signed fine‑art wall prints are offered to every Life &amp; Legacy client. Curated, printed, and finished by hand.
                        </p>
                        <ul className="mt-6 space-y-2 text-sm">
                            {[
                                "Hand‑bound silk or linen albums",
                                "Archival fine‑art papers",
                                "Limited and open editions",
                                "Custom framing in walnut, oak, brass, or matte black",
                            ].map((x) => (
                                <li key={x} className="flex items-start gap-3">
                                    <Check className="h-4 w-4 mt-1 text-[hsl(var(--lux-brass))]" />
                                    <span>{x}</span>
                                </li>
                            ))}
                        </ul>
                        <Link to="/atelier">
                            <Button variant="ghost" className="mt-7 rounded-full px-5 h-10 text-[11px] tracking-[0.22em] uppercase border border-border hover:border-[hsl(var(--lux-brass))]">
                                Explore Fine‑Art Prints
                            </Button>
                        </Link>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-3 gap-3">
                        {heirloom.map((p) => (
                            <EditorialImage key={p.id} src={p.image} alt={p.title} ratio={4 / 5} />
                        ))}
                    </div>
                </div>
            </Section>

            {/* PROCESS */}
            <Section spacing="normal">
                <Kicker>How the experience unfolds</Kicker>
                <SerifTitle className="mt-3">A calm, considered process.</SerifTitle>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-5">
                    {[
                        { n: "01", t: "Inquire", c: "Share your vision through a short, private intake." },
                        { n: "02", t: "Concept", c: "A private call, a moodboard, and an intentional plan." },
                        { n: "03", t: "Session", c: "Calm, guided direction, styling support, and the photographs." },
                        { n: "04", t: "Gallery & Prints", c: "A retouched private gallery, with optional albums and wall prints." },
                    ].map((p) => (
                        <div key={p.n} className="bg-[hsl(var(--lux-porcelain))] border border-border rounded-[2px] p-6">
                            <div className="kicker">{p.n}</div>
                            <div className="font-serif-display text-xl mt-2">{p.t}</div>
                            <Separator className="my-4 bg-[hsl(var(--lux-brass))]/40 w-8" />
                            <p className="text-sm text-muted-foreground">{p.c}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* REVIEWS */}
            <Section spacing="normal" className="border-t border-border">
                <Kicker>From clients</Kicker>
                <SerifTitle className="mt-3">In their own words.</SerifTitle>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                    {lifeReviews.map((r) => (
                        <article key={r.id} className="bg-[hsl(var(--lux-porcelain))] border border-border rounded-[2px] p-7">
                            <p className="font-serif-display text-lg leading-[1.45]">“{r.quote}”</p>
                            <Separator className="my-5 bg-[hsl(var(--lux-brass))]/50 w-8" />
                            <div className="text-xs tracking-[0.16em] uppercase">{r.name}</div>
                            <div className="text-xs text-muted-foreground mt-1">{r.type}</div>
                        </article>
                    ))}
                </div>
            </Section>

            {/* CTA */}
            <Section spacing="normal" className="text-center bg-[hsl(var(--lux-porcelain))] border-t border-border">
                <SerifTitle className="max-w-[22ch] mx-auto">Begin a Life &amp; Legacy session.</SerifTitle>
                <p className="mt-5 text-sm text-muted-foreground max-w-[58ch] mx-auto">
                    A limited number of sessions and commissions are accepted each month to preserve a high‑touch experience.
                </p>
                <Link to="/inquire?type=Story%20Session" className="btn-request mt-7 inline-flex">
                    Request Availability
                </Link>
            </Section>
        </div>
    );
}
